import qwest from 'qwest';
import moment from 'moment';
import {cachePromise} from 'helpers/cachePromise';

let onSuccess = () => {
  return (xhr, response) => {
    if (response.success) {
      return response.data;
    }
    throw response;
  };
};

let onError = () => {
  throw {
    code: -1,
    data: null,
    message: 'Unexpected Error',
    success: false,
  };
};

const dashboardCachePeriod = 1800 * 1000;

let DashboardApi = {

  getDashboardData: cachePromise((id, start, end) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/Summary?branchList=${id}&start=${start}&end=${end}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getFollowUpTile: cachePromise((id, start, end) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.post(`v1/newReports/Dashboard/FollowUpTile?branchList=${id}&start=${start}&end=${end}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getFollowUp: cachePromise((id, start, end, timeframe) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.post(`v1/newReports/Dashboard/FollowUpReport?branchList=${id}` +
      `&start=${start}&end=${end}&groupby=${timeframe}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getPastDueAnalysisTile: cachePromise((id, start, end) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/PastDueAnalysisTile?branchList=${id}&start=${start}&end=${end}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getPastDueAnalysis: cachePromise((id, pastWStr, past2WStr, nextWStr, next2WStr) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/PastDueAnalysis?branchList=${id}&pastWStr=${pastWStr}&past2WStr=${past2WStr}
    &nextWStr=${nextWStr}&next2WStr=${next2WStr}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getAnalysesByEmployeeTile: cachePromise((id, start, end) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/AnalysesByEmployeeTile?branchList=${id}&start=${start}&end=${end}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getAnalysesByEmployee: cachePromise((id, start, end) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/AnalysesByEmployee?branchList=${id}&start=${start}&end=${end}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),


  getAvgWaterPerAccountTile: cachePromise((id, start, end) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/AvgWaterPerAccountTile?branchList=${id}&start=${start}&end=${end}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getAvgWaterPerAccount: cachePromise((id, start, end, interval) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/AvgWaterPerAccount?branchList=${id}&start=${start}&end=${end}&interval=${interval}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  totalWaterAnalysis: cachePromise((id, start, end, start2, end2, groupby) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/TotalWaterAnalysis?branchList=${id}&groupby=${groupby}&start=${start}&end=${end}&start2=${start2}&end2=${end2}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  totalWaterAnalysisTile: cachePromise((id, start, end, groupby) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/TotalWaterAnalysisTile?branchList=${id}&start=${start}&end=${end}&groupby=${groupby}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  accountWithEmailsTile: cachePromise(id => {
    return qwest.get(`v1/newReports/Dashboard/AccountWithEmailsTile?branchList=${id}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getDataQuality: cachePromise((getTruBlueData, branchIds = [], poolTypeFilter = null, start = moment().subtract(1, 'year'), end = moment()) => {
    let branchList = DashboardApi.formatListURLParameter('branchList', branchIds);

    return qwest.get(`v1/newReports/Dashboard/DataQuality?${branchList}&getTruBlue=${getTruBlueData ? getTruBlueData : false}`, {
      poolTypeFilter: poolTypeFilter,
      start: start.hour(0).minute(0).second(0).millisecond(0).format(),
      end: end.hour(23).minute(59).second(59).millisecond(0).format(),
    }, {
      dataType: 'post',
    }).then(DashboardApi.responseHandler);
  }, dashboardCachePeriod),

  getSalesPerformanceTile: cachePromise((id, start, end, start2, end2) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/SalesPerformanceTile?branchList=${id}&start=${start}&end=${end}&start2=${start2}&end2=${end2}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getSalesPerformance: cachePromise((id, start, end, start2, end2, groupby) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/SalesPerformance?branchList=${id}&groupby=${groupby}&start=${start}&end=${end}&start2=${start2}&end2=${end2}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getBioGuardTotalSalesTile: cachePromise((id, start, end) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/BioGuardTotalSalesTile?branchList=${id}&start=${start}&end=${end}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  getBioGuardTotalSales: cachePromise((id, start, end) => {
    if (typeof id === 'object') {
      id = id.join('&branchList=');
    }
    return qwest.get(`v1/newReports/Dashboard/BioGuardTotalSales?branchList=${id}&start=${start}&end=${end}`)
      .then(onSuccess(), onError);
  }, dashboardCachePeriod),

  initialize() {
    return qwest.get('v1/newReports/version')
      .then(onSuccess(), onError);
  },

  formatListURLParameter(name, items) {
    return `${name}=${items.join(`&${name}=`)}`;
  },

  responseHandler(xhr, response) {
    if (response.success) {
      return response.data;
    }
    throw response;
  },
};

export default DashboardApi;
